import { Button, TableRow } from '@mui/material';
import EmptyListBlock from 'components/features/list/EmptyListBlock';
import PaginationBlock from 'components/features/pagination/PaginationBlock';
import PermissionGuard from 'components/guards/component/PermissionGuard';
import { PlusIcon } from 'components/ui/icons';
import TableHeaderCell from 'components/ui/tables/TableHeaderCell';
import TableVariant1 from 'components/ui/tables/TableVariant1';
import { MODEL_PERMISSION } from 'configs/permissions/modelPermissions';
import useDI from 'hooks/useDI';
import useLocales from 'hooks/useLocales';
import useStableNavigate from 'hooks/useStableNavigate';
import React from 'react';
import { useParams } from 'react-router';
import { PATH_PARAM_TOKEN } from 'services/routing/PATH_PARAM';
import { RoutingServiceDashboard } from 'services/routing/RoutingService';
import { useSelector } from 'storage';
import MODEL_NAME from 'typings/models/_model.enum';
import ModelUtils from 'utils/models/ModelUtils';
import DocumentTemplateListItem from './DocumentTemplateListItem';

type Props = {
  documentTemplateList: DocumentTemplate[];
  pagination?: Pagination;
  isLoading: boolean;
};

function DocumentTemplateBlock({ documentTemplateList, pagination, isLoading }: Props) {
  const { translate } = useLocales();
  const navigate = useStableNavigate();
  const { currentUser } = useSelector((state) => state.auth);
  const pathParams = useParams<typeof PATH_PARAM_TOKEN.modelsDirectory>();

  if (!documentTemplateList.length) {
    return (
      <EmptyListBlock title={translate('pages.documentTemplate.emptyTitle')} subtitle={translate('pages.documentTemplate.emptySubtitle')}>
        <PermissionGuard permission={MODEL_PERMISSION.CAN_CREATE_DOCUMENT_TEMPLATE}>
          <Button
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => navigate(RoutingServiceDashboard.modelsDirectory.documentTemplate.getAddPath(pathParams))}
          >
            {translate('pages.enterpriseDocumentTemplateAdd.title')}
          </Button>
        </PermissionGuard>
      </EmptyListBlock>
    );
  }

  return (
    <>
      <TableVariant1 header={<TableHeader />} isLoading={isLoading} sx={{ mt: 2 }}>
        {documentTemplateList.map((dt) => (
          <DocumentTemplateListItem key={dt.id} documentTemplate={dt} />
        ))}
      </TableVariant1>
      <PaginationBlock pagination={pagination} modelData={{ [MODEL_NAME.documentTemplate]: { tenantId: currentUser?.tenant.id || '' } }} />
    </>
  );
}

function TableHeader() {
  const { mappers } = useDI();
  const { translate } = useLocales();
  return (
    <TableRow>
      <TableHeaderCell sortableByField="name" mapper={mappers.models.documentTemplate}>
        {translate('pages.documentTemplate.name')}
      </TableHeaderCell>
      <TableHeaderCell sortableByField="createdAt" mapper={mappers.models.documentTemplate}>
        {translate('pages.documentTemplate.createdAt')}
      </TableHeaderCell>
      <TableHeaderCell></TableHeaderCell>
    </TableRow>
  );
}

export default React.memo(
  DocumentTemplateBlock,
  (pp, np) => ModelUtils.checkArraysEquality(pp.documentTemplateList, np.documentTemplateList) && pp.isLoading === np.isLoading
);
